/**
 * 前台 HTTP 客户端。
 *
 * 独立于后台的 `@/utils/request`：后台那套与 Element Plus 的弹窗、管理端 store、
 * 传输层加密策略耦合，前台直接复用会把整棵宿主依赖树拖进来。
 *
 * 三类响应必须分开处理，混在一起会把「业务失败」误判为「网络失败」：
 *
 *   1. 框架业务端点 `/portal/*`、`/digital/*`
 *      → HTTP 200 + `{ code, msg, data }`；`code !== 200` 即失败，领域码在 `data`
 *   2. 认证端点 `/auth/*`
 *      → 真实 HTTP 401 + `{ error: { code, message } }`（绕过框架信封）
 *   3. 媒体/H5
 *      → 原生 HTTP 语义（404/416），由 `<img>`/`<iframe>` 直接消费，不经本客户端
 */

import axios, { AxiosError, type AxiosRequestConfig } from 'axios'

/** 后端 API 前缀：dev `/dev-api`，docker `/docker-api`，生产 `/prod-api`。 */
export const API_BASE: string = import.meta.env.VITE_APP_BASE_API ?? ''

/** 拼装可直接用于 `src`/`href` 的绝对地址。 */
export function apiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`
}

/** 携带后端领域码的错误。`code` 取自响应体 `data`（框架信封）或 `error.code`（认证端点）。 */
export class PortalApiError extends Error {
  readonly status?: number
  readonly code: string
  readonly payload: unknown

  constructor(message: string, code: string, payload: unknown, status?: number) {
    super(message)
    this.name = 'PortalApiError'
    this.code = code
    this.payload = payload
    this.status = status
  }
}

interface Envelope {
  code?: number
  msg?: string
  data?: unknown
}

interface AuthErrorBody {
  error?: { code?: string; message?: string }
}

const http = axios.create({
  baseURL: API_BASE,
  timeout: 15_000,
  // 会话 Cookie 由后端以 HttpOnly 下发；前台不读取、不持久化令牌
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

function describe(payload: unknown, status: number | undefined): { message: string; code: string } {
  const auth = payload as AuthErrorBody | undefined
  if (auth?.error?.code) {
    return { message: auth.error.message ?? '认证失败', code: auth.error.code }
  }
  const envelope = payload as Envelope | undefined
  if (typeof envelope?.msg === 'string' && envelope.msg) {
    // 框架把领域码放在 data；不是字符串时退回 HTTP 状态码
    const domain = typeof envelope.data === 'string' ? envelope.data : `http_${status ?? 0}`
    return { message: envelope.msg, code: domain }
  }
  // 错误文案面向最终用户，不暴露 HTTP 状态码这类实现细节；
  // 状态码仍保留在 `code` 与 `status` 上供调用方分支判断。
  const fallback =
    status === undefined
      ? '网络连接异常，请检查网络后重试'
      : status >= 500
        ? '服务暂时不可用，请稍后重试'
        : '请求未能完成，请稍后重试'
  return { message: fallback, code: `http_${status ?? 0}` }
}

async function request<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const response = await http.request<Envelope>(config)
    const body = response.data

    // 认证端点是扁平响应体，没有 code 字段 —— 直接返回
    if (typeof body?.code !== 'number') return body as T

    if (body.code !== 200) {
      const { message, code } = describe(body, response.status)
      throw new PortalApiError(message, code, body, response.status)
    }
    // 框架的 `ResponseUtil.success(data=X)` 把业务体放在 `data` 下；`model_content`
    // 则把字段并到信封顶层。前台端点统一用 `data`，故解包后返回 —— 不解包会让
    // 调用方拿到整只信封，`{ authorizationUrl }` 解构出 undefined。
    return (body.data ?? null) as T
  } catch (error) {
    if (error instanceof PortalApiError) throw error
    const axiosError = error as AxiosError
    const status = axiosError.response?.status
    const payload = axiosError.response?.data
    const { message, code } = describe(payload, status)
    throw new PortalApiError(message, code, payload, status)
  }
}

export const api = {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return request<T>({ ...config, url, method: 'get' })
  },
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return request<T>({ ...config, url, method: 'post', data })
  },
  put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return request<T>({ ...config, url, method: 'put', data })
  },
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return request<T>({ ...config, url, method: 'delete' })
  },
}

/* -------------------------------- CSRF 写请求 -------------------------------- */

/**
 * CSRF 令牌缓存。
 *
 * 令牌与会话 Cookie 分处两处：Cookie 按 `SameSite=Lax` 不会跟随跨站写请求，
 * 令牌必须由前端显式放进 `X-CSRF-Token`，两者一致才证明写请求来自本页会话。
 * 缓存只存模块内存，不落 localStorage —— 页面刷新即重新获取。
 */
let csrfToken: string | null = null
let csrfRequest: Promise<string> | null = null

function loadCsrfToken(): Promise<string> {
  if (csrfToken) return Promise.resolve(csrfToken)
  // 并发写请求只取一次令牌，避免同页多个动作各发一轮请求
  csrfRequest ??= api
    .get<{ csrfToken: string }>('/portal/csrf')
    .then((body) => {
      csrfToken = body.csrfToken
      return body.csrfToken
    })
    .finally(() => {
      csrfRequest = null
    })
  return csrfRequest
}

/** 丢弃缓存令牌；会话失效或令牌不匹配后由下一次写请求重新获取。 */
export function clearCsrfToken(): void {
  csrfToken = null
  csrfRequest = null
}

/**
 * 携带 CSRF 令牌的写请求。
 *
 * 会话或令牌失效时只清空缓存、不自动重试：把一次用户动作放大成两次副作用，
 * 在「收藏/完成」这类写端点上会直接改写业务事实。调用方据领域码提示重新登录。
 */
export async function csrfWrite<T>(
  method: 'post' | 'put' | 'delete',
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const token = await loadCsrfToken()
  try {
    return await request<T>({
      ...config,
      url,
      method,
      data,
      headers: { ...config?.headers, 'X-CSRF-Token': token },
    })
  } catch (error) {
    if (
      error instanceof PortalApiError &&
      (error.code === 'portal_session_invalid' || error.code === 'portal_csrf_invalid')
    ) {
      clearCsrfToken()
    }
    throw error
  }
}
